import type { ColumnDef } from '@tanstack/react-table';
import type {
  ColumnVisibility,
  DataTableColumn,
  DataTableColumnGroupingModel,
  DataTableLeafColumn,
  DataTableRowSelectionModel,
  SearchValue,
  SearchValues,
  SortConfig,
  TableData
} from './types';
import { DATA_TABLE_BLANK_FILTER_VALUE } from '@/lib/dynamic-filters';
export const INDEX_COLUMN_WIDTH = 50;
export const SELECTION_COLUMN_WIDTH = 42;
export const ROW_HEIGHT = 34;
export const VIRTUALIZATION_OVERSCAN = 12;
export const VIRTUALIZATION_THRESHOLD = 120;
const DATA_TABLE_ROW_THEME = {
  rowSelected: 'bg-[#ffff99]',
  rowEven: 'bg-white hover:bg-[#ffff99]',
  rowOdd: 'bg-[#fafafa] hover:bg-[#ffff99]',
  stickyEven: 'bg-white group-hover:bg-[#ffff99]',
  stickyOdd: 'bg-[#fafafa] group-hover:bg-[#ffff99]'
} as const;

export const EMPTY_TABLE: TableData = {
  name: '',
  rows: [],
  columns: []
};

export const getColumnWidth = (column?: DataTableColumn) => {
  if (!column) return 120;
  if (typeof column.width === 'number') return column.width;
  if (typeof (column as any).minWidth === 'number') return (column as any).minWidth;
  if (typeof column.flex === 'number') return Math.max(120, Math.round(column.flex * 140));
  return 120;
};

export const buildColumnVisibility = (tables: TableData[]): ColumnVisibility => {
  const nextVisibility: ColumnVisibility = {};

  for (const table of tables) {
    for (const column of table.columns) {
      nextVisibility[column.field] = true;
    }
  }

  return nextVisibility;
};

export const mergeColumnVisibilityState = (
  previousVisibility: ColumnVisibility,
  nextVisibility: ColumnVisibility
): ColumnVisibility => {
  const mergedVisibility: ColumnVisibility = {};
  let hasChanged = false;

  const nextFields = Object.keys(nextVisibility);
  const previousFields = Object.keys(previousVisibility);

  if (previousFields.length !== nextFields.length) {
    hasChanged = true;
  }

  for (const field of nextFields) {
    const resolvedVisibility = previousVisibility[field] ?? nextVisibility[field];
    mergedVisibility[field] = resolvedVisibility;

    if (previousVisibility[field] !== resolvedVisibility) {
      hasChanged = true;
    }
  }

  return hasChanged ? mergedVisibility : previousVisibility;
};

const hasStableRowId = (value: unknown) => value !== undefined && value !== null && value !== '';

export const formatRows = (rows: any[] = []) => {
  if (!Array.isArray(rows)) return [];
  const checkStable = (row: any) =>
    hasStableRowId(row?.uuid) ||
    hasStableRowId(row?.id) ||
    hasStableRowId(row?.ID) ||
    hasStableRowId(row?.code) ||
    hasStableRowId(row?.ma);

  return rows.every(checkStable)
    ? rows
    : rows.map((row, index) => {
        if (checkStable(row)) {
          return row;
        }

        return { ...row, id: index.toString() };
      });
};

export const resolveSelectionModel = (selectionModel?: DataTableRowSelectionModel) =>
  (selectionModel ?? []).map(value => String(value));

export const getResolvedActiveTab = (tables: TableData[], activeTab: number, fallbackTab = 0) => {
  if (tables.length === 0) return 0;
  if (tables[activeTab]) return activeTab;
  return Math.min(Math.max(fallbackTab, 0), tables.length - 1);
};

export const buildLeafColumns = (
  columns: DataTableColumn[],
  columnVisibility: ColumnVisibility,
  checkboxSelection: boolean,
  showIndexColumn: boolean,
  indexColumnWidth = INDEX_COLUMN_WIDTH
) => {
  const nextColumns: DataTableLeafColumn[] = [];

  if (showIndexColumn) {
    nextColumns.push({
      id: '__index__',
      field: '__index__',
      sticky: 'index',
      width: indexColumnWidth
    });
  }

  if (checkboxSelection) {
    nextColumns.push({
      id: '__selection__',
      field: '__selection__',
      sticky: 'selection',
      width: SELECTION_COLUMN_WIDTH
    });
  }

  for (const column of columns) {
    if (columnVisibility[column.field] === false) continue;

    nextColumns.push({
      id: column.field,
      field: column.field,
      width: getColumnWidth(column),
      column
    });
  }

  return nextColumns;
};

export const getStickyLeftOffset = (
  sticky: DataTableLeafColumn['sticky'],
  checkboxSelection: boolean,
  showIndexColumn: boolean
) => {
  if (sticky === 'index') {
    return 0;
  }

  if (sticky === 'selection') {
    return checkboxSelection && showIndexColumn ? INDEX_COLUMN_WIDTH : 0;
  }

  return 0;
};

export const buildTableColumns = (leafColumns: DataTableLeafColumn[]): ColumnDef<any>[] =>
  leafColumns.map(column => ({
    id: column.id,
    accessorFn: row => {
      if (column.sticky === 'selection') return row;
      if (column.sticky === 'index') return row?.id;
      return row?.[column.field];
    },
    meta: column satisfies DataTableLeafColumn,
    size: column.width,
    minSize:
      column.sticky || !column.column
        ? column.width
        : typeof (column.column as any).minWidth === 'number'
          ? (column.column as any).minWidth
          : 60,
    maxSize:
      column.sticky || !column.column
        ? column.width
        : typeof (column.column as any).maxWidth === 'number'
          ? (column.column as any).maxWidth
          : Number.MAX_SAFE_INTEGER,
    enableResizing: !column.sticky && (column.column as any)?.resizable !== false
  }));

interface ResolvedColumnValue {
  rawValue: unknown;
  value: unknown;
}

export const buildDataTableCellParams = (
  row: any,
  rowId: string,
  column: DataTableColumn,
  rawValue: unknown,
  value: unknown
) => ({
  id: rowId,
  field: column.field,
  value: rawValue,
  formattedValue: value,
  row,
  colDef: column,
  hasFocus: false,
  tabIndex: -1,
  api: {} as any
});

const shouldUseResolvedValue = (resolvedValue: unknown, rawValue: unknown) => {
  if (resolvedValue === undefined) return false;
  if (typeof resolvedValue === 'number' && Number.isNaN(resolvedValue)) return false;
  if (resolvedValue === '' && rawValue !== '' && rawValue !== null && rawValue !== undefined) return false;
  return true;
};

const resolveWithCandidates = (
  callback: ((...args: any[]) => unknown) | undefined,
  candidates: Array<() => unknown>,
  rawValue: unknown
) => {
  if (!callback) return rawValue;

  for (const candidate of candidates) {
    try {
      const resolvedValue = candidate();

      if (shouldUseResolvedValue(resolvedValue, rawValue)) {
        return resolvedValue;
      }
    } catch {
      continue;
    }
  }

  return rawValue;
};

const resolveValueGetter = (row: any, rowId: string, column: DataTableColumn, rawValue: unknown) =>
  resolveWithCandidates(
    column.valueGetter as ((...args: any[]) => unknown) | undefined,
    [
      () => (column.valueGetter as any)?.(rawValue, row, column, null),
      () => (column.valueGetter as any)?.(buildDataTableCellParams(row, rowId, column, rawValue, rawValue)),
      () => (column.valueGetter as any)?.(row, column.field, rawValue, rowId)
    ],
    rawValue
  );

const resolveValueFormatter = (row: any, rowId: string, column: DataTableColumn, value: unknown, rawValue: unknown) =>
  resolveWithCandidates(
    column.valueFormatter as ((...args: any[]) => unknown) | undefined,
    [
      () => (column.valueFormatter as any)?.(value),
      () => (column.valueFormatter as any)?.(buildDataTableCellParams(row, rowId, column, rawValue, value)),
      () => (column.valueFormatter as any)?.(value, row, column, null)
    ],
    value
  );

const SEARCHABLE_OBJECT_KEYS = [
  'ma_vt',
  'ten_vt',
  'dvt',
  'ten_dvt',
  'ma_kho',
  'ma_vi_tri',
  'code',
  'name',
  'ten',
  'label',
  'so_ct',
  'ma_bp',
  'ma_vu_viec',
  'ma_hd',
  'ma_dtt',
  'ma_ku',
  'ma_phi',
  'ma_lsx',
  'ma_cp',
  'ma_cpkhl'
];

const isSearchablePrimitive = (value: unknown) =>
  ['string', 'number', 'boolean'].includes(typeof value) && value !== '';

const hasDisplayablePrimitiveValue = (value: unknown) =>
  isSearchablePrimitive(value) && !(typeof value === 'number' && Number.isNaN(value));

const resolveObjectDisplayValue = (value: unknown, field: string, skipFallback = false): unknown => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;

  const record = value as Record<string, unknown>;

  if (isSearchablePrimitive(record[field])) {
    return record[field];
  }

  if (skipFallback) {
    return undefined;
  }

  for (const key of SEARCHABLE_OBJECT_KEYS) {
    if (isSearchablePrimitive(record[key])) {
      return record[key];
    }
  }

  return undefined;
};

const resolveDirectDataDisplayValue = (row: any, field: string): unknown => {
  if (!row || typeof row !== 'object') return undefined;

  return resolveObjectDisplayValue(row[`${field}_data`], field);
};

const resolveSiblingDataDisplayValue = (row: any, field: string): unknown => {
  if (!row || typeof row !== 'object') return undefined;

  for (const [key, value] of Object.entries(row)) {
    if (!key.endsWith('_data')) continue;
    if (key === `${field}_data`) continue;

    const nestedValue = resolveObjectDisplayValue(value, field, true);

    if (nestedValue !== undefined) {
      return nestedValue;
    }
  }

  return undefined;
};

export const resolveColumnValue = (row: any, column?: DataTableColumn, rowId = ''): ResolvedColumnValue => {
  if (!column) {
    return {
      rawValue: undefined,
      value: undefined
    };
  }

  const rawValue = row?.[column.field];
  const valueAfterGetter = resolveValueGetter(row, rowId, column, rawValue);
  const resolvedValue = resolveValueFormatter(row, rowId, column, valueAfterGetter, rawValue);
  const directDataDisplayValue =
    !column.valueGetter && !column.valueFormatter ? resolveDirectDataDisplayValue(row, column.field) : undefined;
  const siblingDataDisplayValue =
    !column.valueGetter && !column.valueFormatter && !hasDisplayablePrimitiveValue(resolvedValue)
      ? resolveSiblingDataDisplayValue(row, column.field)
      : undefined;

  return {
    rawValue,
    value: directDataDisplayValue ?? siblingDataDisplayValue ?? resolvedValue
  };
};

export const renderDataCell = (row: any, rowId: string, column: DataTableColumn) => {
  const { rawValue, value } = resolveColumnValue(row, column, rowId);

  if (column.renderCell) {
    return column.renderCell(buildDataTableCellParams(row, rowId, column, rawValue, value) as any);
  }

  if (value == null) return '';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
};

const isSearchValueEmpty = (value: SearchValue) =>
  !value || (typeof value === 'string' && value.trim() === '') || (Array.isArray(value) && value.length === 0);

const areSearchValuesEqual = (left: SearchValues, right: SearchValues) => {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);

  if (leftKeys.length !== rightKeys.length) return false;

  for (const key of leftKeys) {
    const leftValue = left[key];
    const rightValue = right[key];

    if (Array.isArray(leftValue) || Array.isArray(rightValue)) {
      if (!Array.isArray(leftValue) || !Array.isArray(rightValue) || leftValue.length !== rightValue.length) {
        return false;
      }

      for (let index = 0; index < leftValue.length; index += 1) {
        if (leftValue[index] !== rightValue[index]) {
          return false;
        }
      }

      continue;
    }

    if (leftValue !== rightValue) {
      return false;
    }
  }

  return true;
};

export const setSearchValueForField = (
  previousValues: SearchValues,
  field: string,
  value: SearchValue
): SearchValues => {
  const nextValues: SearchValues = {
    ...previousValues,
    [field]: value
  };

  if (isSearchValueEmpty(value)) {
    delete nextValues[field];
  }

  return areSearchValuesEqual(previousValues, nextValues) ? previousValues : nextValues;
};

export const matchesSearchValue = (cellValue: unknown, searchValue: SearchValue) => {
  const normalizedValue = cellValue == null ? '' : String(cellValue).toLowerCase();

  if (Array.isArray(searchValue)) {
    return searchValue.some(value => normalizedValue.includes(String(value).toLowerCase()));
  }

  if (!searchValue) return true;

  if (searchValue === DATA_TABLE_BLANK_FILTER_VALUE) {
    return normalizedValue.trim() === '';
  }

  if (searchValue.startsWith('contains:')) {
    return searchValue
      .slice('contains:'.length)
      .split(';')
      .filter(Boolean)
      .some(value => normalizedValue.includes(value.toLowerCase()));
  }

  if (searchValue.includes(';')) {
    return searchValue
      .split(';')
      .filter(Boolean)
      .some(value => normalizedValue.includes(value.toLowerCase()));
  }

  return normalizedValue.includes(searchValue.toLowerCase());
};

export const getSearchDisplayValue = (value: SearchValue | undefined) => {
  if (Array.isArray(value)) return value.join(';');
  if (value === DATA_TABLE_BLANK_FILTER_VALUE) return '(Trống)';
  return value || '';
};

const getComparableNumber = (value: unknown) => {
  if (typeof value === 'number') return Number.isNaN(value) ? null : value;
  if (typeof value !== 'string') return null;

  const normalizedValue = value.replace(/,/g, '').trim();
  if (!normalizedValue) return null;

  const numericValue = Number(normalizedValue);
  return Number.isNaN(numericValue) ? null : numericValue;
};

export const sortRowsWithColumns = (
  rows: any[],
  sortConfig: SortConfig | null,
  columns: DataTableColumn[],
  resolveRowId?: (row: any) => string
) => {
  if (!sortConfig?.direction) return rows;

  const nextRows = [...rows];
  const targetColumn = columns.find(column => column.field === sortConfig.field);

  nextRows.sort((a, b) => {
    const valueA = targetColumn ? resolveColumnValue(a, targetColumn, resolveRowId?.(a)).value : a?.[sortConfig.field];
    const valueB = targetColumn ? resolveColumnValue(b, targetColumn, resolveRowId?.(b)).value : b?.[sortConfig.field];

    const comparableNumberA = targetColumn?.type === 'number' ? getComparableNumber(valueA) : null;
    const comparableNumberB = targetColumn?.type === 'number' ? getComparableNumber(valueB) : null;

    if (comparableNumberA != null && comparableNumberB != null) {
      return sortConfig.direction === 'asc'
        ? comparableNumberA - comparableNumberB
        : comparableNumberB - comparableNumberA;
    }

    const stringA = valueA == null ? '' : String(valueA).toLowerCase();
    const stringB = valueB == null ? '' : String(valueB).toLowerCase();

    return sortConfig.direction === 'asc' ? stringA.localeCompare(stringB) : stringB.localeCompare(stringA);
  });

  return nextRows;
};

export const getTextAlignClass = (align?: DataTableColumn['align']) => {
  if (align === 'right') return 'text-right';
  if (align === 'center') return 'text-center';
  return 'text-left';
};

export const getCellContentAlignClass = (align?: DataTableColumn['align']) => {
  if (align === 'right') return 'justify-end';
  if (align === 'center') return 'justify-center';
  return 'justify-start';
};

export const getHeaderAlignClass = (align?: DataTableColumn['headerAlign']) => {
  if (align === 'right') return 'justify-end text-right';
  if (align === 'left') return 'justify-start text-left';
  return 'justify-center text-center';
};

export const getRowClassName = (isSelected: boolean, rowIndex: number) => {
  if (isSelected) return DATA_TABLE_ROW_THEME.rowSelected;
  return rowIndex % 2 === 0 ? DATA_TABLE_ROW_THEME.rowEven : DATA_TABLE_ROW_THEME.rowOdd;
};

export const getStickyCellClassName = (isSelected: boolean, rowIndex: number) => {
  if (isSelected) return DATA_TABLE_ROW_THEME.rowSelected;
  return rowIndex % 2 === 0 ? DATA_TABLE_ROW_THEME.stickyEven : DATA_TABLE_ROW_THEME.stickyOdd;
};

interface GroupPathEntry {
  key: string;
  label: string;
}

export interface GroupHeaderCell {
  key: string;
  label: string;
  colSpan: number;
  rowSpan: number;
}

export interface GroupableLeafColumn {
  id: string;
  originalColumn?: DataTableColumn;
}

const buildGroupPathMap = (columnGroupingModel?: DataTableColumnGroupingModel): Record<string, GroupPathEntry[]> => {
  const groupPaths: Record<string, GroupPathEntry[]> = {};

  const visitChildren = (children: any[] = [], currentPath: GroupPathEntry[] = []) => {
    children.forEach(child => {
      if ('field' in child) {
        groupPaths[child.field] = currentPath;
        return;
      }

      const nextPath = [
        ...currentPath,
        {
          key: String(child.groupId),
          label: child.headerName || String(child.groupId)
        }
      ];

      visitChildren(child.children || [], nextPath);
    });
  };

  visitChildren(columnGroupingModel || []);
  return groupPaths;
};

const getPathPrefixKey = (path: GroupPathEntry[], depth: number) =>
  path
    .slice(0, depth)
    .map(entry => entry.key)
    .join('>');

export const buildGroupHeaderRows = (
  dataColumns: GroupableLeafColumn[],
  columnGroupingModel?: DataTableColumnGroupingModel
) => {
  const groupPathMap = buildGroupPathMap(columnGroupingModel);
  const columnPaths = dataColumns.map(column => groupPathMap[column.originalColumn?.field || column.id] || []);
  const maxDepth = Math.max(0, ...columnPaths.map(path => path.length));

  if (!maxDepth) {
    return {
      maxDepth: 0,
      rows: [] as GroupHeaderCell[][]
    };
  }

  const occupiedRows = new Array(columnPaths.length).fill(0);
  const rows: GroupHeaderCell[][] = [];

  for (let depth = 0; depth < maxDepth; depth += 1) {
    const rowCells: GroupHeaderCell[] = [];

    for (let columnIndex = 0; columnIndex < columnPaths.length; ) {
      if (occupiedRows[columnIndex] > 0) {
        occupiedRows[columnIndex] -= 1;
        columnIndex += 1;
        continue;
      }

      const currentPath = columnPaths[columnIndex];

      if (currentPath.length <= depth) {
        const rowSpan = maxDepth - depth;

        rowCells.push({
          key: `blank-${depth}-${columnIndex}`,
          label: '',
          colSpan: 1,
          rowSpan
        });

        if (rowSpan > 1) {
          occupiedRows[columnIndex] = rowSpan - 1;
        }

        columnIndex += 1;
        continue;
      }

      const currentPrefixKey = getPathPrefixKey(currentPath, depth + 1);
      let nextColumnIndex = columnIndex + 1;

      while (nextColumnIndex < columnPaths.length) {
        const nextPath = columnPaths[nextColumnIndex];

        if (nextPath.length <= depth || getPathPrefixKey(nextPath, depth + 1) !== currentPrefixKey) {
          break;
        }

        nextColumnIndex += 1;
      }

      const blockPaths = columnPaths.slice(columnIndex, nextColumnIndex);
      const deepestBlockDepth = Math.max(...blockPaths.map(path => path.length));
      const rowSpan = deepestBlockDepth === depth + 1 ? maxDepth - depth : 1;

      if (rowSpan > 1) {
        for (let index = columnIndex; index < nextColumnIndex; index += 1) {
          occupiedRows[index] = rowSpan - 1;
        }
      }

      rowCells.push({
        key: `${currentPrefixKey}-${depth}-${columnIndex}`,
        label: currentPath[depth]?.label || '',
        colSpan: nextColumnIndex - columnIndex,
        rowSpan
      });

      columnIndex = nextColumnIndex;
    }

    rows.push(rowCells);
  }

  return {
    maxDepth,
    rows
  };
};
